import type {
  LeadershipWheel,
  ComponentLeadershipWheelSubTrait,
  LeadershipWheelPage,
} from '@edwin-group/people-graphql';
import { GENERATED } from '@edwin-group/people-graphql';
import { completeTask } from '@/services/daily/complete-tasks.service';
import { create } from 'zustand';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { getLeadershipWheel, getPage } from '@/services/daily/leadership-wheel.service';
import { sanitizeGradientId } from '../utils/sanitizeGradientId';

interface LeadershipWheelStore {
  FocusArea: LeadershipWheel | null;
  SubFocusArea: ComponentLeadershipWheelSubTrait | null;
  slices: {
    id: string;
    focusArea: string | undefined;
    start: string;
    end: string;
  }[];
  focusAreas: LeadershipWheel[];
  leadershipWheelPage: LeadershipWheelPage | null;
  loading: boolean;
  onInit: () => Promise<void>;
  onResetAllAnswers: () => void;
  onUpdateFocusArea: (focusArea: LeadershipWheel | null) => void;
  onUpdateSubFocusArea: (subFocusArea: ComponentLeadershipWheelSubTrait | null) => void;
  onUpdateSlices: (
    slices: LeadershipWheel[] | ComponentLeadershipWheelSubTrait[],
    FocusArea: LeadershipWheel | null,
  ) => void;
}

export const useLeadershipWheelStore = create<LeadershipWheelStore>()((set, get) => ({
  FocusArea: null,
  SubFocusArea: null,
  focusAreas: [],
  slices: [],
  leadershipWheelPage: null,
  loading: false,
  onInit: async () => {
    set({ loading: true });
    const [focusAreasData, page] = await Promise.all([getLeadershipWheel(), getPage()]);
    set({ focusAreas: focusAreasData.focusAreas, leadershipWheelPage: page.page, loading: false });
  },
  onResetAllAnswers: () => {
    const { onUpdateSlices, focusAreas } = get();
    set({ FocusArea: null, SubFocusArea: null });
    onUpdateSlices(focusAreas, null);
  },
  onUpdateFocusArea: (focusArea: LeadershipWheel | null) => {
    set({
      FocusArea: focusArea,
    });
  },
  onUpdateSubFocusArea: async (subFocusArea: ComponentLeadershipWheelSubTrait | null) => {
    const { FocusArea } = get();

    const { error } = await completeTask({
      entity: {
        currentTrait: subFocusArea,
        activeLeadershipWheel: FocusArea,
        activeLeadershipWheelSubTrait: subFocusArea,
      },
      type: GENERATED.Enum_Completedtask_Type.LeadershipWheel,
    });

    trackGAEvent('leadership_wheel_logged', {
      trait: FocusArea?.attributes?.focusArea ?? FocusArea?.focusArea ?? '',
      sub_trait: subFocusArea?.attributes?.focusArea ?? subFocusArea?.focusArea ?? '',
    });

    set({
      SubFocusArea: subFocusArea,
    });

    if (error) {
      return;
    }
  },
  onUpdateSlices: (
    slices: LeadershipWheel[] | ComponentLeadershipWheelSubTrait[],
    FocusArea: LeadershipWheel | null,
  ) => {
    const gradient1From = FocusArea?.attributes?.typeSection.mainGradientFrom;
    const gradient1To = FocusArea?.attributes?.typeSection.mainGradientTo;
    const gradient2From = FocusArea?.attributes?.typeSection.secondaryGradientFrom;
    const gradient2To = FocusArea?.attributes?.typeSection.secondaryGradientTo;

    if (slices.length > 0) {
      set({
        slices: slices.map((focusAreaItem, index) => {
          if ('attributes' in focusAreaItem) {
            return {
              id: sanitizeGradientId(`gradient${focusAreaItem.attributes?.focusArea}`),
              focusArea: focusAreaItem.attributes?.focusArea,
              start: '#' + (focusAreaItem.attributes as LeadershipWheel)?.typeSection.mainGradientFrom,
              end: '#' + (focusAreaItem.attributes as LeadershipWheel)?.typeSection.mainGradientTo,
            };
          }

          if ('focusArea' in focusAreaItem) {
            return {
              id: sanitizeGradientId(`gradient${(focusAreaItem as LeadershipWheel)?.focusArea}`),
              focusArea: (focusAreaItem as LeadershipWheel)?.focusArea,
              start: '#' + (index % 2 === 0 ? gradient1From : gradient2From),
              end: '#' + (index % 2 === 0 ? gradient1To : gradient2To),
            };
          }

          return {
            id: '',
            focusArea: '',
            start: '',
            end: '',
          };
        }),
      });
    }
  },
}));
